// src/pages/Dashboard/Admin/Coaching/components/CoachingSummaryPanel.jsx

import React, { useMemo } from "react";
import { FaCalendarAlt, FaClock } from "react-icons/fa";

import {
  buildMonthlySummary,
  formatHours,
} from "../utils/coachingDashboardUtils";

export default function CoachingSummaryPanel({ events, loading }) {
  const rows = useMemo(() => buildMonthlySummary(events), [events]);

  const total = useMemo(() => {
    return rows.reduce((sum, row) => sum + Number(row.hours || 0), 0);
  }, [rows]);

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Récapitulatif
            </p>

            <h2 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
              <FaCalendarAlt className="text-indigo-600 dark:text-indigo-300" />
              Heures par mois
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Regroupement mensuel des heures de coaching de l’enseignant
              sélectionné.
            </p>
          </div>

          <div className="inline-flex items-center gap-2 rounded-2xl bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20">
            <FaClock />
            Total : {formatHours(total)}
          </div>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, index) => (
              <div
                key={index}
                className="h-14 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
              />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-[22px] border border-slate-200 bg-slate-50 px-5 py-10 text-center text-sm text-slate-600 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300">
            Aucun récapitulatif disponible pour cet enseignant.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-[22px] border border-slate-200 dark:border-slate-800">
            <table className="min-w-[620px] w-full">
              <thead className="bg-slate-50 text-xs uppercase tracking-[0.14em] text-slate-500 dark:bg-slate-950/70 dark:text-slate-400">
                <tr>
                  <th className="px-4 py-3 text-left font-bold">Mois</th>
                  <th className="px-4 py-3 text-left font-bold">Événements</th>
                  <th className="px-4 py-3 text-left font-bold">Heures</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
                {rows.map((row) => (
                  <tr
                    key={row.key}
                    className="transition hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  >
                    <td className="px-4 py-4 font-bold text-slate-900 dark:text-white">
                      {row.label}
                    </td>

                    <td className="px-4 py-4 text-slate-700 dark:text-slate-200">
                      {row.events}
                    </td>

                    <td className="px-4 py-4 font-bold text-slate-900 dark:text-white">
                      {formatHours(row.hours)}
                    </td>
                  </tr>
                ))}
              </tbody>

              <tfoot>
                <tr className="bg-slate-950 text-white dark:bg-slate-800">
                  <td className="px-4 py-4 text-xs font-bold uppercase tracking-[0.14em]">
                    Total
                  </td>

                  <td className="px-4 py-4 text-xs font-bold">
                    {rows.reduce((sum, row) => sum + row.events, 0)}
                  </td>

                  <td className="px-4 py-4 text-xs font-bold">
                    {formatHours(total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}